import { z } from 'zod'
import {
  assessCostCoverage,
  priceBasisStatus,
  priceEvidence,
  priceSourceTag,
} from './cost-evidence'
import {
  calculateScaleLadder,
  costScenarioInputSchema,
  exactToNumber,
  SCALE_LADDER_QUANTITIES,
  SCALE_LADDER_VERSION,
  SCALE_OUTLAY_IDS,
  type calculateCostScenario,
  type CostScenarioInput,
  type ExactValue,
  type ScaleLadderInput,
  type ScaleOutlayId,
} from './cost-scenario'
import type { QuoteResult } from './schemas/quotes'
import type { SimulationPublic } from './schemas/simulation'
import type { TastingSource } from './schemas/tastings'
import { SOURCE_TAGS, type SourceTag } from './source-type'
import type { SpecReadiness } from './spec-sheet'
import {
  VALIDATION_CONDITIONS,
  VALIDATION_STAGES,
  type ValidationStageView,
} from './validation-stage'

/**
 * '더 만들어도 남을까' 첫 카드 (대표 Top5 #1, 2026-10-30). 제품 검토 리포트 맨 위에 한 장으로 답한다:
 * "이 맛을 더 많이 만들어도 같은 품질로 유지하고 돈을 남길 수 있을까?"
 * - 남는 돈: 규모별 손익 사다리(cost-scenario.ts 의 calculateScaleLadder)
 * - 같은 품질: 확인 필요 조건 목록 (충족/확인 필요, 점수·확률 없음)
 * - 지금 검증 단계 (validation-stage.ts)
 * 사다리 입력은 실제 기록에서 고른다: 공장 견적(고른 견적) > 저장한 원가 시나리오 > 결과 화면 원가(추정). 값마다 근거 꼬리표.
 */
export const SCALE_CHECK_VERSION = `scale-check-v1+${SCALE_LADDER_VERSION}` as const

/** 사용자가 첫 카드에서 바꿔 넣는 값 (모두 선택). null 은 '모름'으로 되돌리기 */
export const scaleLadderOverridesSchema = z
  .object({
    /** 낱개 판매가 (결과 통화, 세금 별도) */
    unitPrice: z.number().finite().nonnegative().max(1e9).nullable().optional(),
    /** 판매 채널 수수료 (0.1 = 10%). 직접 팔면 0 */
    channelFeeRate: z.number().finite().min(0).max(1).nullable().optional(),
    moq: z.number().int().positive().max(10_000_000).nullable().optional(),
    /** 초기 지출. 해당 없으면 0 */
    outlays: z
      .object(
        Object.fromEntries(
          SCALE_OUTLAY_IDS.map((id) => [
            id,
            z.number().finite().nonnegative().max(1e10).nullable().optional(),
          ]),
        ) as Record<ScaleOutlayId, z.ZodOptional<z.ZodNullable<z.ZodNumber>>>,
      )
      .strict()
      .optional(),
    quantities: z.array(z.number().int().positive().max(10_000_000)).min(1).max(6).optional(),
  })
  .strict()
export type ScaleLadderOverrides = z.infer<typeof scaleLadderOverridesSchema>

type ScenarioCalculation = ReturnType<typeof calculateCostScenario>

/**
 * POST /simulations/:id/cost-scenario 본문 (Top10 #7). 앱의 원가 시나리오 칸 입력(공용 커널 입력 그대로)과 첫 카드에서 바꾼 값.
 * 서버가 다시 계산해 배합 버전마다 저장한다 — 앱이 낸 계산 결과는 받지 않는다. 둘 다 없으면 저장된 값 없이 기본값으로 계산한다
 */
export const costScenarioRunRequestSchema = z
  .object({
    scenario: costScenarioInputSchema.nullable().optional(),
    ladder: scaleLadderOverridesSchema.optional(),
  })
  .strict()
export type CostScenarioRunRequest = z.infer<typeof costScenarioRunRequestSchema>

/** cost_scenario_runs.run 에 저장하는 본문 — 입력과 서버 계산을 함께 남긴다 */
export interface CostScenarioRunBody {
  schemaVersion: 1
  scenario: { input: CostScenarioInput; calculation: ScenarioCalculation } | null
  overrides: ScaleLadderOverrides
  ladderInput: ScaleLadderInput
  ladder: ReturnType<typeof calculateScaleLadder>
}

/** POST /simulations/:id/cost-scenario 응답의 run */
export interface CostScenarioRunView extends CostScenarioRunBody {
  id: string
  simulationId: string
  formulaVersionId: string | null
  resultRevision: string
  createdAt: string
}

export interface ScaleLadderSources {
  result: SimulationPublic
  /** 이 배합 버전에 저장한 원가 시나리오의 입력과 계산 (없으면 null) */
  scenario: { input: CostScenarioInput; calculation: ScenarioCalculation } | null
  /** 사용자가 고른 공장 견적 (없으면 null) */
  offer: QuoteResult | null
  /** 이 제품으로 결제한 시제품(샘플) 금액 중 결과 통화와 같은 것 (없으면 null) */
  paidPrototype: number | null
  overrides: ScaleLadderOverrides
}

const tagged = (amount: number | null, source: SourceTag) =>
  amount === null ? { amount: null, source: 'unconfirmed' as const } : { amount, source }

/** 결과 화면 원가의 꼬리표: 단가가 모두 사람이 넣은 값이면 계산, 하나라도 추정이면 AI 추정, 빠지면 미확인 */
function resultUnitCost(result: SimulationPublic) {
  if (!assessCostCoverage(result).complete) return tagged(null, 'unconfirmed')
  const entered = [
    ...result.ingredients.map((i) => priceEvidence(i.unitPrice, i.priceEvidence)),
    priceEvidence(result.costs.packagingPerUnit, result.costs.evidence?.packaging),
    priceEvidence(result.costs.laborPerUnit, result.costs.evidence?.labor),
  ].every((e) => e.source === 'user_input')
  return tagged(result.costs.totalPerUnit, entered ? 'calculated' : 'ai_estimated')
}

const exactNumber = (value: ExactValue | null | undefined) => (value ? exactToNumber(value) : null)

/** 실제 기록과 사용자 값으로 사다리 입력을 만든다. 같은 재료면 같은 입력 */
export function buildScaleLadderInput(sources: ScaleLadderSources): ScaleLadderInput {
  const { result, scenario, overrides } = sources
  const offer = sources.offer?.currency === result.currency ? sources.offer : null
  const scenarioUnitCost = exactNumber(scenario?.calculation.result?.basePhysicalUnitCost)
  const scenarioPrice = scenario ? scenarioUnitPrice(scenario.input) : null
  const scenarioFee = scenario?.input.sellingLines?.find(
    (line) => line.type === 'rate' && line.fraction !== null,
  )
  const unitCost = offer
    ? tagged(offer.unitPrice, 'supplier_quote')
    : scenarioUnitCost !== null
      ? tagged(scenarioUnitCost, 'calculated')
      : resultUnitCost(result)
  const unitPrice =
    overrides.unitPrice !== undefined
      ? tagged(overrides.unitPrice, 'user_input')
      : scenarioPrice !== null
        ? tagged(scenarioPrice, 'user_input')
        : tagged(result.pricing.suggestedRetail || null, 'ai_estimated')
  const channelFeeRate =
    overrides.channelFeeRate !== undefined
      ? overrides.channelFeeRate === null
        ? { fraction: null, source: 'unconfirmed' as const }
        : { fraction: overrides.channelFeeRate, source: 'user_input' as const }
      : scenarioFee?.type === 'rate'
        ? { fraction: scenarioFee.fraction, source: 'user_input' as const }
        : { fraction: null, source: 'unconfirmed' as const }
  const oneTime = offer?.lines.filter((line) => line.frequency === 'one_time') ?? []
  const outlay = (id: ScaleOutlayId) => {
    const entered = overrides.outlays?.[id]
    if (entered !== undefined) return tagged(entered, 'user_input')
    if (id === 'prototype') {
      if (sources.paidPrototype !== null) return tagged(sources.paidPrototype, 'supplier_quote')
      if (offer?.sample?.availability === 'free') return tagged(0, 'supplier_quote')
      return tagged(offer?.sample?.price ?? null, 'supplier_quote')
    }
    if (id === 'development' && oneTime.length)
      return tagged(
        oneTime.reduce((total, line) => total + line.amount, 0),
        'supplier_quote',
      )
    return tagged(null, 'unconfirmed')
  }
  const moq =
    overrides.moq !== undefined
      ? overrides.moq === null
        ? { count: null, source: 'unconfirmed' as const }
        : { count: overrides.moq, source: 'user_input' as const }
      : offer && offer.moq > 0
        ? { count: offer.moq, source: 'supplier_quote' as const }
        : { count: null, source: 'unconfirmed' as const }
  return {
    currency: result.currency,
    unitCost,
    unitPrice,
    channelFeeRate,
    runCosts: (offer?.lines ?? [])
      .filter((line) => line.frequency === 'per_batch')
      .map((line, index) => ({
        id: `batch_${index + 1}`,
        amount: tagged(line.amount, 'supplier_quote'),
      })),
    outlays: SCALE_OUTLAY_IDS.map((id) => ({ id, amount: outlay(id) })),
    moq,
    quantities: overrides.quantities ?? [...SCALE_LADDER_QUANTITIES],
  }
}

/**
 * 시나리오에서 사용자가 넣은 1팩 판매가를 낱개 값으로 (사다리는 낱개로 센다). 단가가 없거나 세금 기준이 맞지 않으면 null.
 * 화면 표시용 금액이라 부동소수 나눗셈으로 충분하다 — 사다리 계산은 이 값을 다시 정확한 분수로 받는다
 */
function scenarioUnitPrice(input: CostScenarioInput): number | null {
  const price = input.pricePerSalesPack
  if (
    !price ||
    price.amount === null ||
    price.currency !== input.currency ||
    price.taxBasis !== 'exclusive' ||
    !input.physicalUnitsPerSalesPack ||
    priceEvidence(price.amount, price.evidence).status === 'missing'
  )
    return null
  return price.amount / input.physicalUnitsPerSalesPack
}

// ── 같은 품질로 유지할 조건 ────────────────────────────────────────────────

export const SCALE_QUALITY_CONDITIONS = [
  'formula_saved',
  'quantities_confirmed',
  'prices_confirmed',
  'tasted',
  'factory_sample_tasted',
  'process_scale_up',
  'shelf_life_verified',
  'moq_known',
] as const
export type ScaleQualityCondition = (typeof SCALE_QUALITY_CONDITIONS)[number]

export interface ScaleCheckInput {
  result: SimulationPublic
  ladderInput: ScaleLadderInput
  stage: ValidationStageView
  tastings: readonly TastingSource[]
  /** 단가 유효기간 판정의 오늘 (YYYY-MM-DD) */
  today: string
}

const exactValueSchema = z
  .object({ numerator: z.string().max(80), denominator: z.string().max(80) })
  .strict()
const sourceTagSchema = z.enum(SOURCE_TAGS)
const rowSchema = z
  .object({
    quantity: z.number().int().positive(),
    /** 실제로 만드는 수량 (MOQ 아래면 MOQ) */
    producedQuantity: z.number().int().positive(),
    moqApplied: z.boolean(),
    isMoq: z.boolean(),
    costPerSoldUnit: exactValueSchema,
    contribution: exactValueSchema,
    contributionRatio: exactValueSchema.nullable(),
  })
  .strict()

/** 리포트에 저장하는 첫 카드. 같은 재료면 같은 값 */
export const scaleCheckSchema = z
  .object({
    version: z.string().max(80),
    stage: z.enum(VALIDATION_STAGES),
    /** 다음 검증 단계에 필요한 조건 */
    stageMissing: z.array(z.enum(VALIDATION_CONDITIONS)).max(VALIDATION_CONDITIONS.length),
    /** 같은 품질로 유지할 조건 — met false 면 '확인 필요' */
    conditions: z
      .array(
        z
          .object({
            code: z.enum(SCALE_QUALITY_CONDITIONS),
            met: z.boolean(),
            source: sourceTagSchema,
            /** 개수 (확인 안 된 단가 수, 시식 수 등). 없으면 null */
            count: z.number().int().min(0).nullable(),
          })
          .strict(),
      )
      .max(SCALE_QUALITY_CONDITIONS.length),
    /** 남는 돈: positive 모든 칸에서 낱개당 남음 · negative 어느 칸도 안 남음 · mixed 규모에 따라 다름 · unknown 계산 보류 */
    profit: z.enum(['positive', 'mixed', 'negative', 'unknown']),
    ladder: z
      .object({
        status: z.enum(['complete', 'incomplete', 'invalid']),
        currency: z.enum(['KRW', 'USD']),
        sources: z
          .object({
            unitCost: sourceTagSchema,
            unitPrice: sourceTagSchema,
            channelFee: sourceTagSchema,
            moq: sourceTagSchema,
            outlays: z
              .array(z.object({ id: z.string().max(60), source: sourceTagSchema }).strict())
              .max(20),
          })
          .strict(),
        /** 사다리 입력의 원래 금액 (화면이 '무엇으로 계산했는지' 보인다) */
        unitCost: z.number().nullable(),
        unitPrice: z.number().nullable(),
        channelFeeRate: z.number().nullable(),
        outlays: z
          .array(z.object({ id: z.string().max(60), amount: z.number().nullable() }).strict())
          .max(20),
        /** 실은 초기 지출 합계 (아는 것만) */
        outlayTotal: z.number().nonnegative(),
        /** 초기 지출을 다 알았는지. 거짓이면 회수 판매량을 보류한다 */
        outlaysComplete: z.boolean(),
        moq: z.number().int().nullable(),
        sellingFeesIncluded: z.boolean(),
        /** 초기 지출을 되찾는 최소 판매 개수. 모르면 null */
        paybackUnits: z.string().max(40).nullable(),
        rows: z.array(rowSchema).max(10),
      })
      .strict(),
  })
  .strict()
export type ScaleCheck = z.infer<typeof scaleCheckSchema>

type QualityInput = Omit<ScaleCheckInput, 'ladderInput'> & {
  /** MOQ 근거. 모르면(사양서처럼 손익을 보지 않는 곳) null — moq_known 조건을 빼고 낸다 */
  moq: ScaleLadderInput['moq'] | null
}

/** 같은 품질로 유지할 조건 (첫 카드·사양서가 같이 쓴다) */
function scaleQualityConditions(input: QualityInput): ScaleCheck['conditions'] {
  const { result, stage, tastings, today, moq } = input
  const prices = result.ingredients.map((i) => ({
    tag: priceSourceTag(i.unitPrice, i.priceEvidence, i.priceBasis),
    expired:
      priceEvidence(i.unitPrice, i.priceEvidence).source === 'user_input' &&
      priceBasisStatus(i.priceBasis, today) === 'expired',
  }))
  const unconfirmedPrices = prices.filter(
    (p) => p.expired || p.tag === 'ai_estimated' || p.tag === 'unconfirmed',
  ).length
  const coverage = assessCostCoverage(result)
  const needsCheckQty = result.ingredients.filter((i) => i.qtyStatus === 'needs_check').length
  const factoryTastings = tastings.filter((s) => s === 'sample' || s === 'first_production').length
  const firstProduction = tastings.filter((s) => s === 'first_production').length
  const formulaSaved = stage.steps
    .find((s) => s.stage === 'formula_set')!
    .conditions.every((c) => c.met)
  const pricesOk = unconfirmedPrices === 0 && coverage.packagingKnown && coverage.laborKnown
  const conditions: ScaleCheck['conditions'] = [
    {
      code: 'formula_saved',
      met: formulaSaved,
      source: formulaSaved ? 'user_input' : 'unconfirmed',
      count: null,
    },
    {
      code: 'quantities_confirmed',
      met: needsCheckQty === 0,
      source: needsCheckQty === 0 ? 'user_input' : 'ai_estimated',
      count: needsCheckQty,
    },
    {
      code: 'prices_confirmed',
      met: pricesOk,
      source: pricesOk ? 'user_input' : unconfirmedPrices ? 'ai_estimated' : 'unconfirmed',
      count: unconfirmedPrices,
    },
    {
      code: 'tasted',
      met: tastings.length > 0,
      source: tastings.length ? 'sensory_panel' : 'unconfirmed',
      count: tastings.length,
    },
    {
      code: 'factory_sample_tasted',
      met: factoryTastings > 0,
      source: factoryTastings ? 'sensory_panel' : 'unconfirmed',
      count: factoryTastings,
    },
    // 배합 중량을 늘리는 것(선형 환산)과 공정 시간·온도·혼합을 늘리는 것은 다르다 — 실제 생산분을 맛봐야 충족
    {
      code: 'process_scale_up',
      met: firstProduction > 0,
      source: firstProduction ? 'sensory_panel' : 'unconfirmed',
      count: null,
    },
    // 보관 방법·소비기한은 생성 때 AI 초안이고, 보관 시험을 기록하는 곳이 아직 없다
    { code: 'shelf_life_verified', met: false, source: 'ai_estimated', count: null },
  ]
  if (moq)
    conditions.push({
      code: 'moq_known',
      met: moq.count !== null && moq.source !== 'unconfirmed',
      source: moq.source,
      count: null,
    })
  return conditions
}

/** 사양서(spec-sheet.ts)에 넣는 검증 단계·확인 필요 조건. 손익은 보지 않아 MOQ 조건은 뺀다 */
export function specReadiness(input: Omit<QualityInput, 'moq'>): SpecReadiness {
  return {
    stage: input.stage.stage,
    stageMissing: input.stage.missing,
    needsCheck: scaleQualityConditions({ ...input, moq: null })
      .filter((c) => !c.met)
      .map((c) => c.code),
  }
}

/** 첫 카드를 만든다. AI 를 부르지 않는 순수 함수 */
export function buildScaleCheck(input: ScaleCheckInput): ScaleCheck {
  const { ladderInput, stage } = input
  const ladder = calculateScaleLadder(ladderInput)
  const conditions = scaleQualityConditions({ ...input, moq: ladderInput.moq })
  const rows = ladder.result?.rows ?? []
  // 낱개당 남는 돈이 없으면 어느 규모도 안 남고, 있으면 수량마다 초기 지출을 실은 남는 돈의 부호로 본다
  const positive = rows.map((r) => exactToNumber(r.contribution) > 0)
  const profit = ladder.result?.noPositiveContribution
    ? 'negative'
    : rows.length === 0
      ? 'unknown'
      : positive.every(Boolean)
        ? 'positive'
        : positive.some(Boolean)
          ? 'mixed'
          : 'negative'
  return scaleCheckSchema.parse({
    version: SCALE_CHECK_VERSION,
    stage: stage.stage,
    stageMissing: stage.missing,
    conditions,
    profit,
    ladder: {
      status: ladder.status,
      currency: ladderInput.currency,
      sources: {
        unitCost: ladderInput.unitCost.source,
        unitPrice: ladderInput.unitPrice.source,
        channelFee: ladder.result?.sources.channelFee ?? 'unconfirmed',
        moq: ladderInput.moq.source,
        outlays: ladderInput.outlays.map((o) => ({ id: o.id, source: o.amount.source })),
      },
      unitCost: ladderInput.unitCost.amount,
      unitPrice: ladderInput.unitPrice.amount,
      channelFeeRate: ladderInput.channelFeeRate.fraction,
      outlays: ladderInput.outlays.map((o) => ({ id: o.id, amount: o.amount.amount })),
      outlayTotal: ladder.result?.outlayTotal ?? 0,
      outlaysComplete: ladder.result?.outlaysComplete ?? false,
      moq: ladder.result?.moq ?? null,
      sellingFeesIncluded: ladder.result?.sellingFeesIncluded ?? false,
      paybackUnits: ladder.result?.paybackUnits ?? null,
      rows,
    },
  })
}
